import { useQuery } from "@tanstack/react-query";
import type { InvitePeek } from "@goalhorns/shared";
import { useI18n } from "../i18n/I18nProvider";
import { ErrorState } from "../components/ui/ErrorState";
import { apiRequest } from "../lib/api";
import { paths } from "../lib/endpoints";
import { AuthShell } from "./AuthShell";
import { DevLoginForm } from "./DevLoginForm";
import { GoogleSignInLink } from "./GoogleSignInLink";
import { devLoginAvailableQuery } from "./queries";

export interface InvitePageProps {
  token: string;
}

/**
 * /invite/:token. A valid invite shows whose invitation it is and the workspace name, then starts
 * Google sign-in with the token. Every invalid state (expired, used, revoked, unknown) gets the same message.
 */
export function InvitePage({ token }: InvitePageProps) {
  const { t } = useI18n();
  const peek = useQuery({
    queryKey: ["invite-peek", token] as const,
    queryFn: () => apiRequest<InvitePeek>(paths.invitePeek(token)),
    retry: false,
  });
  const devLogin = useQuery(devLoginAvailableQuery);
  const data = peek.data;
  const invalid = peek.isError || (data !== undefined && !data.valid);

  return (
    <AuthShell>
      <header className="space-y-2 text-center">
        <h1 className="font-display text-3xl leading-none font-extrabold tracking-[0.01em] uppercase">
          {t("auth.invite.title")}
        </h1>
        {data?.valid && data.workspaceName ? (
          <p className="font-display text-display-md leading-[0.92] font-extrabold tracking-[0.01em] text-blue uppercase">
            {data.workspaceName}
          </p>
        ) : null}
      </header>
      {peek.isPending ? (
        <p className="text-center text-operate text-ink-2">{t("app.loading")}</p>
      ) : null}
      {data?.valid ? (
        <>
          <p className="rounded-control bg-ice-2 p-3 text-note text-ink-2">
            {t("invitePage.for", {
              email: data.email,
              workspace: data.workspaceName ?? t("invitePage.noWorkspace"),
            })}
          </p>
          <GoogleSignInLink href={`${paths.googleStart}?invite=${encodeURIComponent(token)}`} />
          {devLogin.data ? <DevLoginForm invite={token} /> : null}
        </>
      ) : null}
      {invalid ? (
        <ErrorState>
          <span className="block font-semibold">{t("invitePage.invalidTitle")}</span>
          <span className="block">{t("auth.error.invite_invalid")}</span>
          <span className="block">{t("invitePage.invalidText")}</span>
        </ErrorState>
      ) : null}
    </AuthShell>
  );
}
